import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../api/apiService';
import './TablePages.css';

const Products: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [brandSuggestions, setBrandSuggestions] = useState<string[]>([]);
  const [stockModal, setStockModal] = useState<{ id: number; name: string; stock: number | null } | null>(null);
  const [stockInput, setStockInput] = useState('');
  const [stockSaving, setStockSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nameFilter.trim()) params.set('name', nameFilter.trim());
      if (brandFilter.trim()) params.set('brand', brandFilter.trim());
      const url = params.toString() ? `/admin/products?${params.toString()}` : '/admin/products';
      const { data } = await apiService.get(url);
      setList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    Promise.all([
      apiService.get('/admin/products/names').then(({ data }) => setNameSuggestions(Array.isArray(data) ? data : [])),
      apiService.get('/admin/brands').then(({ data }) => setBrandSuggestions(Array.isArray(data) ? data.map((b: { name: string }) => b.name) : [])),
    ]).catch(() => {});
  }, []);

  const formatDate = (s: string) => (s ? new Date(s).toLocaleString() : '-');

  if (loading) return <p>Loading...</p>;

  return (
    <div className="table-page">
      <div className="table-toolbar">
        <div className="table-toolbar-filters">
          <label htmlFor="product-name-filter">Name</label>
          <input
            id="product-name-filter"
            type="text"
            className="table-filter-input"
            placeholder="Filter by name..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            list="product-name-suggestions"
          />
          <datalist id="product-name-suggestions">
            {nameSuggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <label htmlFor="product-brand-filter">Brand</label>
          <input
            id="product-brand-filter"
            type="text"
            className="table-filter-input"
            placeholder="Filter by brand..."
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            list="product-brand-suggestions"
          />
          <datalist id="product-brand-suggestions">
            {brandSuggestions.map((brand) => (
              <option key={brand} value={brand} />
            ))}
          </datalist>
        </div>
        <div className="table-toolbar-right">
          <button type="button" onClick={load} className="btn-primary">Refresh</button>
        </div>
      </div>
      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Brand</th>
              <th>Size</th>
              <th>Price</th>
              <th>Stock</th>
              <th>Distributor</th>
              <th>Available</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {list.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.brand ?? '-'}</td>
                <td>{row.liter_size != null ? `${row.liter_size}L` : '-'}</td>
                <td>₹{row.price}</td>
                <td>
                  {row.stock != null ? row.stock : '-'}
                </td>
                <td>{row.distributor_name ?? '-'}</td>
                <td>{row.is_available ? 'Yes' : 'No'}</td>
                <td>{formatDate(row.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {stockModal && (
        <div className="modal-overlay" onClick={() => !stockSaving && setStockModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Update stock: {stockModal.name}</h3>
            <input
              type="number"
              min={0}
              value={stockInput}
              onChange={(e) => setStockInput(e.target.value)}
              placeholder="Stock quantity"
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn-primary"
                disabled={stockSaving}
                onClick={async () => {
                  const val = parseInt(stockInput.trim(), 10);
                  if (isNaN(val) || val < 0) return;
                  setStockSaving(true);
                  try {
                    await apiService.patch(`/admin/products/${stockModal.id}`, { stock: val });
                    setStockModal(null);
                    load();
                  } catch (e) {
                    console.error(e);
                  } finally {
                    setStockSaving(false);
                  }
                }}
              >
                {stockSaving ? 'Saving...' : 'Save'}
              </button>
              <button type="button" className="btn-secondary" disabled={stockSaving} onClick={() => setStockModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
